import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FloppyDisk, Microphone, Plus, Stop, Trash, UploadSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_IMAGE_MAX_DIMENSION = 1600;
const AUTO_IMAGE_WEBP_QUALITY = 0.82;

const newRow = (orderId = 1) => ({
  id: `${Date.now()}-${Math.random()}`,
  order_id: orderId,
  type: 'text',
  text: '',
  url: null,
  mime_type: null,
  uploading: false,
  recording: false
});

export default function InquiryAutoMessages({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recordingId, setRecordingId] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    axios.get(`${API}/mb-config/inquiry-auto-messages`)
      .then((response) => setRows((response.data.messages || []).map((item, index) => ({
        ...newRow(Number(item.order_id) || index + 1),
        order_id: Number(item.order_id) || index + 1,
        type: item.type || 'text',
        text: item.text || '',
        url: item.url || null,
        mime_type: item.mime_type || null
      }))))
      .catch(() => toast.error('Failed to load inquiry auto messages'))
      .finally(() => setLoading(false));

    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    };
  }, []);

  const updateRow = (id, field, value) => {
    setRows((current) => current.map((row) => row.id === id ? { ...row, [field]: value } : row));
  };

  const prepareAutoImage = (file) => new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please select an image file'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const scale = Math.min(1, AUTO_IMAGE_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Image conversion is not supported in this browser');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            reject(new Error('Could not convert image to WebP'));
            return;
          }
          resolve(new File([blob], 'inquiry-auto.webp', { type: 'image/webp' }));
        }, 'image/webp', AUTO_IMAGE_WEBP_QUALITY);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('This image format cannot be converted by the browser'));
    };
    image.src = objectUrl;
  });

  const uploadFile = async (rowId, file, type) => {
    if (!file) return;
    updateRow(rowId, 'uploading', true);
    try {
      const uploadFileValue = type === 'image' ? await prepareAutoImage(file) : file;
      const formData = new FormData();
      formData.append('file', uploadFileValue, uploadFileValue.name || `inquiry-auto.${type === 'audio' ? 'webm' : 'webp'}`);
      formData.append('folder', 'inquiry-auto-messages');
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploaded = response.data?.file || response.data;
      if (!uploaded?.url) throw new Error('Upload did not return a URL');
      updateRow(rowId, 'url', uploaded.url);
      updateRow(rowId, 'mime_type', uploaded.mimeType || uploadFileValue.type || null);
      toast.success(type === 'image' ? 'Image resized and converted to WebP' : 'Audio uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to upload ${type}`);
    } finally {
      updateRow(rowId, 'uploading', false);
    }
  };

  const startRecording = async (rowId) => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        toast.error('Audio recording is not supported in this browser');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => event.data.size && chunksRef.current.push(event.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setRecordingId(null);
        await uploadFile(rowId, new File([blob], 'inquiry-auto.webm', { type: 'audio/webm' }), 'audio');
      };
      recorder.start();
      setRecordingId(rowId);
      updateRow(rowId, 'recording', true);
    } catch (error) {
      toast.error('Microphone permission is required to record audio');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    if (recordingId) updateRow(recordingId, 'recording', false);
  };

  const addRow = () => {
    const next = rows.reduce((max, row) => Math.max(max, Number(row.order_id) || 0), 0) + 1;
    setRows((current) => [...current, newRow(next)]);
  };

  const save = async () => {
    const seen = new Set();
    for (const row of rows) {
      const orderId = Number(row.order_id);
      if (!Number.isInteger(orderId) || orderId < 1) {
        toast.error('Every Order ID must be a positive number');
        return;
      }
      if (seen.has(orderId)) {
        toast.error(`Order ID ${orderId} already exists. Please add another Order ID.`);
        return;
      }
      seen.add(orderId);
      if (row.type === 'text' && !row.text.trim()) {
        toast.error(`Text is required for Order ID ${orderId}`);
        return;
      }
      if (row.type !== 'text' && !row.url) {
        toast.error(`Please upload ${row.type} for Order ID ${orderId}`);
        return;
      }
      if (row.uploading || row.recording) {
        toast.error(`Please wait for Order ID ${orderId} media to finish`);
        return;
      }
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/mb-config/inquiry-auto-messages`, {
        messages: rows.map(({ order_id, type, text, url, mime_type }) => ({ order_id: Number(order_id), type, text, url, mime_type }))
      });
      setRows((response.data.messages || []).map((item) => ({ ...newRow(item.order_id), ...item })));
      toast.success('Inquiry auto messages saved');
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.error || 'Failed to save inquiry auto messages';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex items-center justify-between gap-3 border-b bg-white px-4 py-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
          <ArrowLeft size={18} /> Back
        </button>
        <button type="button" onClick={save} disabled={saving || loading} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
          <FloppyDisk size={17} weight="bold" /> {saving ? 'Saving…' : 'Save Inquiry Auto Messages'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">Inquiry Auto Messages</h2>
            <p className="mt-1 text-xs text-slate-500">These messages are sent once, in Order ID order, after the customer’s first inquiry or support message.</p>
          </div>

          {loading ? <div className="rounded-xl bg-white p-8 text-center text-sm text-slate-500">Loading…</div> : rows.map((row) => (
            <div key={row.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-slate-600">Order ID</label>
                  <input type="number" min="1" value={row.order_id} onChange={(e) => updateRow(row.id, 'order_id', e.target.value)} className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                  <select value={row.type} onChange={(e) => { updateRow(row.id, 'type', e.target.value); updateRow(row.id, 'url', null); updateRow(row.id, 'text', ''); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                    <option value="text">Text</option>
                    <option value="audio">Audio</option>
                    <option value="image">Image</option>
                  </select>
                </div>
                <button type="button" onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50">
                  <Trash size={16} /> Delete
                </button>
              </div>

              {row.type === 'text' && <textarea value={row.text} onChange={(e) => updateRow(row.id, 'text', e.target.value)} rows={3} placeholder="Enter automatic text message" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />}

              {row.type === 'image' && (
                <div className="space-y-2">
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/heic" disabled={row.uploading} onChange={(e) => uploadFile(row.id, e.target.files?.[0], 'image')} className="block w-full text-sm" />
                  {row.url && <img src={row.url} alt="Auto message preview" className="max-h-40 max-w-xs rounded-lg border object-contain" />}
                  {row.uploading && <p className="text-xs text-slate-500">Uploading image…</p>}
                </div>
              )}

              {row.type === 'audio' && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
                      <UploadSimple size={16} /> Upload audio
                      <input type="file" accept="audio/*" disabled={row.uploading || row.recording} onChange={(e) => uploadFile(row.id, e.target.files?.[0], 'audio')} className="hidden" />
                    </label>
                    {!row.recording ? <button type="button" onClick={() => startRecording(row.id)} disabled={row.uploading || recordingId !== null} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"><Microphone size={16} /> Record audio</button> : <button type="button" onClick={stopRecording} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white"><Stop size={16} /> Stop recording</button>}
                  </div>
                  {row.url && <audio controls src={row.url} className="w-full max-w-md" />}
                  {row.uploading && <p className="text-xs text-slate-500">Uploading audio…</p>}
                </div>
              )}
            </div>
          ))}

          {!loading && <button type="button" onClick={addRow} className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-100"><Plus size={17} /> Add Auto Message</button>}
        </div>
      </div>
    </div>
  );
}
